import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MessageKey } from "@tovi/shared";
import { Button, Modal, StatusBadge, useI18n, useTranslate } from "@tovi/ui";
import { api } from "@/lib/api.ts";
import { subscribeToStoreEvents } from "@/lib/events.ts";
import { speak } from "@/lib/speech.ts";

/**
 * Choosing a table (`08-pos-terminal-and-qr.md` § A, step 1, and § B's table
 * map).
 *
 * "Select table or choose take-away; enter covers (People)". The states are
 * § B's — free, seated, ordered, bill requested — and they are the same four
 * the handheld will show, which is why they live on the table row rather than
 * in this component. Their colours come from `StatusBadge` one step further
 * out again: the back office lists these same tables, and while the till kept
 * a redder set of its own the two disagreed about two of the four.
 *
 * Those states are derived server-side from the table's open bill, so a table
 * seated on another terminal reads as taken here. Tapping one opens the bill
 * that is already on it rather than starting a second, which is also what the
 * diner app does when a party orders again.
 */

type Table = {
  id: string;
  name: string;
  capacity: number;
  status: "free" | "seated" | "ordered" | "bill_requested";
  openOrderId: string | null;
  areaName: string | null;
};

/**
 * A table's state, as a key rather than as the column's own word.
 *
 * `bill_requested` is a database value; printing it — even prettied up with a
 * `replace("_", " ")` — puts English in a Khmer till and the schema's spelling
 * in front of a cashier. The enum crosses as a message key, as every other
 * enum in this product does.
 */
const STATUS = {
  free: "pos.table.status.free",
  seated: "pos.table.status.seated",
  ordered: "pos.table.status.ordered",
  bill_requested: "pos.table.status.billRequested",
} as const;

export function TablePicker({
  onClose,
  onPick,
  onResume,
  isOffline,
}: {
  onClose: () => void;
  onPick: (table: { id: string; name: string }, covers: number) => void;
  onResume: (orderId: string) => void;
  isOffline: boolean;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const [chosen, setChosen] = useState<Table | null>(null);

  const queryClient = useQueryClient();

  const tables = useQuery({
    queryKey: ["tables"],
    queryFn: () => api<{ data: Table[] }>("/store/tables?size=200").then((r) => r.data),
    // Occupancy is somebody else's action, so this list goes stale on its own.
    // The stream below is what notices; the interval is the belt to that pair
    // of braces, and it is slow because it is now only there for the case
    // where the stream is down and the reconnect has not landed yet.
    refetchInterval: isOffline ? false : 60_000,
    refetchOnMount: "always",
  });

  /**
   * The store's realtime feed (`04-api-surface.md` § Realtime).
   *
   * This used to be a ten-second poll, and the reason given was sound at the
   * time: the API's event bus was in-process, so a second instance would leave
   * this terminal quietly out of date — which on a table picker is worse than
   * a poll, because a wrong answer looks like a right one. The bus is fanned
   * out through Redis now, so a table freeing up shows when it frees up.
   *
   * The event carries an id and nothing else, and the response to it is a
   * refetch rather than a patch — § Realtime's own shape. Occupancy is derived
   * from a table's open bill, so anything this handler wrote from a payload
   * would be a second copy of an answer the server computes.
   */
  useEffect(() => {
    if (isOffline) return;
    const stream = subscribeToStoreEvents((event) => {
      if (event.type === "table.changed" || event.type === "orders.new") {
        void queryClient.invalidateQueries({ queryKey: ["tables"] });
      }
      // The server sends the words only where the merchant bought
      // `STORE_SETTINGS_VOICE_BROADCAST` and switched it on, so its presence
      // is the whole decision. It arrives as a message key, because this
      // terminal knows what language it is set to and the server does not.
      if (event.speak) speak(t(event.speak as MessageKey), locale);
    });
    return () => stream.close();
  }, [isOffline, queryClient, t, locale]);

  if (chosen) {
    return (
      <CoverPicker
        table={chosen}
        onClose={onClose}
        onPick={(covers) => onPick({ id: chosen.id, name: chosen.name }, covers)}
      />
    );
  }

  /** Areas, in the order the server sorted them. § B: a map, by area. */
  const areas = new Map<string, Table[]>();
  for (const table of tables.data ?? []) {
    const area = table.areaName ?? "";
    areas.set(area, [...(areas.get(area) ?? []), table]);
  }

  return (
    <Modal open title={t("pos.table.title")} onClose={onClose}>
      <div className="flex flex-col gap-4">
        {[...areas].map(([area, rows]) => (
          <section key={area} className="flex flex-col gap-2">
            {area ? (
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {area}
              </h3>
            ) : null}
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {rows.map((table) => {
                const isTaken = table.status !== "free";
                // A bill opened elsewhere can only be fetched with a
                // connection. Saying so beats a tap that does nothing.
                const isReachable = !isTaken || (Boolean(table.openOrderId) && !isOffline);

                return (
                  <button
                    key={table.id}
                    type="button"
                    disabled={!isReachable}
                    title={isTaken && !isReachable ? t("pos.bill.resumeOffline") : undefined}
                    onClick={() => {
                      if (isTaken) onResume(table.openOrderId!);
                      else setChosen(table);
                    }}
                    className="flex min-h-20 flex-col items-start justify-between rounded-card border border-line bg-surface p-2 text-start disabled:opacity-50"
                  >
                    <span className="text-sm font-semibold text-ink-body">{table.name}</span>
                    <span className="text-xs text-ink-muted">
                      {isTaken && isReachable
                        ? t("pos.bill.openThisBill")
                        : t("pos.table.seats", { count: table.capacity })}
                    </span>
                    <StatusBadge status={table.status} label={t(STATUS[table.status])} />
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Modal>
  );
}

/**
 * Covers.
 *
 * A keypad rather than a text field: `GeneralPopupNumberOfPax` is a number a
 * cashier enters with one hand while seating people with the other. The same
 * pad answers both questions — how many are being seated, and how many turned
 * out to arrive — which is why it takes a table's name rather than its row.
 *
 * Seating one, nothing is pre-selected: the table's capacity is how many it
 * seats, not how many arrived, and a highlighted default is a count nobody
 * counted. Correcting one, `current` is held so the cashier can see what the
 * bill says now and what they are changing it from.
 */
export function CoverPicker({
  table,
  current = null,
  onClose,
  onPick,
}: {
  table: { name: string };
  /** The count already on the bill, when there is one to correct. */
  current?: number | null;
  onClose: () => void;
  onPick: (covers: number) => void;
}) {
  const t = useTranslate();

  return (
    <Modal open title={t("pos.table.howMany", { table: table.name })} onClose={onClose}>
      <div className="grid grid-cols-4 gap-2">
        {Array.from({ length: 12 }, (_, index) => index + 1).map((covers) => (
          <Button
            key={covers}
            variant={covers === current ? "primary" : "secondary"}
            aria-pressed={current === null ? undefined : covers === current}
            onClick={() => onPick(covers)}
          >
            {covers}
          </Button>
        ))}
      </div>
    </Modal>
  );
}
